import { useCallback, useEffect, useMemo } from "react";
import { PowerZones as PowerZonesClient } from "@WahooFitness/cloud-client-ts";
import {
  PowerAlgorithmEnum,
  PowerAlgorithmLookupType,
  PowerZoneResponseType,
} from "@WahooFitness/cloud-client-types";
import { clearNulls } from "@/services/clearNulls";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { StoredZones, Zone, ZoneSet } from "./useZones";
import { t } from "@lingui/macro";
import {
  useUserContext,
  useCloudContext,
  useConfigContext,
  useNativeMessagingContext,
  useOfflineSWR,
} from "@WahooFitness/wahoo-offline-mfe";
import { copyZones } from "./ZonesUtils";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";

const usePowerZones: () => StoredZones = () => {
  const { workoutProfiles, updateProfile, mutateWorkoutProfiles } = useWorkoutProfilesContext();

  const { userIsLoading, userError, mutateUser } = useUserContext();
  const { getCloudClient } = useCloudContext();
  const { getSettingsRemoteConfigObject } = useSettingsRemoteConfig();

  const { wahooToken } = useConfigContext();
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();

  const powerZonesClient = useMemo(() => getCloudClient(PowerZonesClient), [getCloudClient]);

  const {
    data: powerZoneData,
    error: powerZoneError,
    isLoading: powerZoneLoading,
    mutate: mutatePowerZones,
  } = useOfflineSWR(["getPowerZones", wahooToken], async ([_key, wahooToken]) => {
    return clearNulls(await powerZonesClient.get(wahooToken));
  });

  useEffect(() => {
    const refreshListener = addRefreshListener(mutatePowerZones);
    return () => removeRefreshListener(refreshListener);
  }, [addRefreshListener, mutatePowerZones, removeRefreshListener]);

  const extractZonesForDisplay = useCallback((zoneSet: PowerZoneResponseType) => {
    const zoneCount =
      zoneSet.zone_count ||
      Object.entries(zoneSet).filter(([key, value]) => key.startsWith("zone_") && value !== null)
        .length;
    return Array.from(Array(zoneCount).keys()).map((key) => {
      const prevKey = `zone_${key}` as keyof PowerZoneResponseType;
      const currKey = `zone_${key + 1}` as keyof PowerZoneResponseType;
      return {
        bottom: key ? Number(zoneSet[prevKey]) + 1 : 0,
        top: key === zoneCount - 1 ? ("max" as Zone["top"]) : Number(zoneSet[currKey]) || 0,
        name: t`Zone ${key + 1}`,
      };
    });
  }, []);

  const savedZoneSets: Record<number, ZoneSet> = useMemo(() => {
    const zoneSetMap = {} as Record<number, ZoneSet>;
    if (!powerZoneData) {
      return zoneSetMap;
    }
    for (const zoneSetIndex in powerZoneData) {
      const zoneSet = powerZoneData[zoneSetIndex];
      const zones = extractZonesForDisplay(zoneSet);
      zoneSetMap[zoneSet.id] = {
        ...zoneSet,
        zones,
        displayIndex: +zoneSetIndex,
        name: zoneSet.name || t`Set ${+zoneSetIndex + 1}`,
        zone_count: zones.length,
      };
    }
    return zoneSetMap;
  }, [powerZoneData, extractZonesForDisplay]);

  const extractZonesForSave = useCallback((zoneSet: ZoneSet) => {
    const {
      zones,
      zone_1: _zone_1,
      zone_2: _zone_2,
      zone_3: _zone_3,
      zone_4: _zone_4,
      zone_5: _zone_5,
      zone_6: _zone_6,
      zone_7: _zone_7,
      zone_8: _zone_8,
      zone_9: _zone_9,
      zone_10: _zone_10,
      ...rest
    } = zoneSet;
    const update: Omit<ZoneSet, "zones"> = rest || {};
    zones.forEach((zone, index) => {
      if (index > 0) {
        (update[`zone_${index}` as keyof Omit<ZoneSet, "zones">] as number) = zone.bottom - 1;
      }
    });
    update.zone_count = zones.length;
    return update;
  }, []);

  const updatedAt = useMemo(() => {
    const updatedAtMap = {} as Record<number, Date | undefined>;
    if (!powerZoneData) {
      return updatedAtMap;
    }
    for (const zoneSet of powerZoneData) {
      updatedAtMap[zoneSet.id] = (zoneSet.updated_at && new Date(zoneSet.updated_at)) || undefined;
    }
    return updatedAtMap;
  }, [powerZoneData]);

  const updateZoneSet = useCallback(
    async (zoneSet: ZoneSet) => {
      const update = extractZonesForSave(zoneSet);
      if (wahooToken && zoneSet.id) {
        const newZoneSet = await powerZonesClient.put(wahooToken, zoneSet.id, update);
        mutatePowerZones(
          powerZoneData?.map((set) => {
            if (set.id === zoneSet.id) {
              return { ...set, ...update };
            }
            return set;
          })
        );
        return newZoneSet;
      }
    },
    [extractZonesForSave, mutatePowerZones, powerZoneData, powerZonesClient, wahooToken]
  );

  const createZoneSet = useCallback(
    async (zoneSet: ZoneSet) => {
      const update = extractZonesForSave(zoneSet);
      if (wahooToken) {
        const newZoneSet = await powerZonesClient.post(wahooToken, update);
        const newZoneSets = [...(powerZoneData || []), newZoneSet];
        mutatePowerZones(newZoneSets, { revalidate: false });
        mutateUser();
        return newZoneSet;
      }
    },
    [extractZonesForSave, mutatePowerZones, mutateUser, powerZoneData, powerZonesClient, wahooToken]
  );

  const saveZoneSet = useCallback(
    async (zoneSet: ZoneSet) => {
      if (zoneSet.id === -1) {
        return createZoneSet(zoneSet);
      } else {
        return updateZoneSet(zoneSet);
      }
    },
    [updateZoneSet, createZoneSet]
  );

  const primaryZoneSetId = useMemo(
    () => powerZoneData?.find((zone) => zone.primary)?.id,
    [powerZoneData]
  );

  const deleteZoneSet = useCallback(
    async (zoneSetId: number) => {
      await Promise.all(
        workoutProfiles
          .filter((profile) => profile.heart_rate_zone_id === zoneSetId)
          .map(
            async (profile) =>
              await updateProfile(profile.external_id, "heart_rate_zone_id", primaryZoneSetId)
          )
      );
      await powerZonesClient.delete(wahooToken, zoneSetId);
      if (powerZoneData) {
        mutatePowerZones(powerZoneData.filter((zoneSet) => zoneSet.id !== zoneSetId));
        mutateUser();
      }
      mutateWorkoutProfiles();
    },
    [
      mutatePowerZones,
      mutateUser,
      mutateWorkoutProfiles,
      powerZoneData,
      powerZonesClient,
      primaryZoneSetId,
      updateProfile,
      wahooToken,
      workoutProfiles,
    ]
  );

  const clearAlgorithmInfo = useCallback(
    (zoneSets: Record<number, ZoneSet>, _zoneSetId: number) => {
      return zoneSets;
    },
    []
  );

  const autoCalcZoneSet = useCallback(
    async (currentZoneSet: ZoneSet, params: PowerAlgorithmLookupType) => {
      if (wahooToken) {
        const newZoneSet = await powerZonesClient.algorithmLookup(wahooToken, {
          power_zone_algorithm_id: params.ftp ? PowerAlgorithmEnum.FTP : undefined,
          zone_count: params.zone_count || 7,
          ftp: params.ftp,
        });
        return {
          ...currentZoneSet,
          ...newZoneSet,
          // cloud is passing back a decimal, but doesn't accept a decimal
          ftp: newZoneSet.ftp ? Math.round(newZoneSet.ftp) : undefined,
          zones: extractZonesForDisplay(newZoneSet),
        };
      }
    },
    [extractZonesForDisplay, powerZonesClient, wahooToken]
  );

  const maxZoneValue = Infinity;

  const zoneCountChoices = useMemo(
    () => getSettingsRemoteConfigObject<number[]>("POWER_ZONE_COUNT_CHOICES") ?? [6, 7, 8],
    [getSettingsRemoteConfigObject]
  );

  const autoCalculateVariant = "ftp";

  const profileField = "power_zone_id";

  const reloadZones = useCallback(() => mutateUser(), [mutateUser]);

  const maxAutoCalculateValues = {};

  const createValidZones = useCallback(
    (zoneSetId: number, startingIndex: number, zoneSets: Record<number, ZoneSet>) => {
      const correctedZones = copyZones(zoneSets);
      const zoneSet = correctedZones[zoneSetId].zones;
      zoneSet[startingIndex].bottom = Math.round(zoneSet[startingIndex].bottom);
      zoneSet[startingIndex - 1].top = zoneSet[startingIndex].bottom - 1;

      // ensure zones are in ascending order. Assumes only the zone at startingIndex
      // has been changed, and the zones were in a valid state before the change
      for (let i = startingIndex; i < zoneSet.length; i++) {
        if (zoneSet[i].bottom <= zoneSet[i - 1].bottom) {
          zoneSet[i].bottom = +zoneSet[i - 1].bottom + 1;
          zoneSet[i - 1].top = +zoneSet[i - 1].bottom;
          // if the value at the starting index was just corrected, there is no need to continue.
          // The values above the starting index can't be less than the value at the starting index if the starting index value was lowered.
          if (i === startingIndex) {
            break;
          }
        }
        // if the value at startingIndex was already above the value below it, it may have been raised, so the values above it need to be checked
        else if (i === startingIndex) {
          continue;
        }
        // if i is an index above startingIndex, and the value was already above the value below it, then everything should already be ascending
        else {
          break;
        }
      }

      //ensure corrections didn't put zones above the max value
      for (let i = zoneSet.length - 1; i > 0; i--) {
        const topValue = zoneSet[i].top === "max" ? maxZoneValue : zoneSet[i].top;
        if (typeof topValue === "number" && zoneSet[i].bottom > topValue) {
          zoneSet[i].bottom = +topValue;
          zoneSet[i - 1].top = +topValue - 1;
        } else {
          break;
        }
      }
      return correctedZones;
    },
    [maxZoneValue]
  );

  const zonesSetIsValid: (zoneSet: ZoneSet, zoneCount: number) => boolean = useCallback(
    (zoneSet: ZoneSet, zoneCount: number) => {
      return (
        zoneSet.zones.every(
          (zone, index, array) => index === 0 || zone.bottom > array[index - 1].bottom
        ) && zoneCountChoices?.includes(zoneCount)
      );
    },
    [zoneCountChoices]
  );

  return {
    savedZoneSets,
    updatedAt,
    saveZoneSet,
    maxZoneValue,
    zoneCountChoices,
    autoCalculateVariant,
    primaryZoneSetId,
    profileField,
    autoCalcZoneSet,
    deleteZoneSet,
    clearAlgorithmInfo,
    error: userError || powerZoneError,
    isLoading: userIsLoading || powerZoneLoading,
    reloadZones,
    maxAutoCalculateValues,
    zonesSetIsValid,
    createValidZones,
    extractZonesForDisplay,
  };
};

export default usePowerZones;
